// =============================================================================
// Teck Meng Construction Supply — child theme behaviors.
// Mirrors the interactive JS from the HTML mockup (nav, dropdown, form, sort).
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {
  // --- Mobile nav drawer -----------------------------------------------------
  // The nav is server-rendered from the chosen WP menu (site-nav block); at
  // <=1080px it becomes a right-side drawer with scrim, accordion sub-menus,
  // scroll lock and Esc/scrim/close/link-click dismissal.
  const mainNav = document.getElementById('mainNav');
  const burgerBtn = document.getElementById('burgerBtn');
  const navScrim = document.querySelector('[data-tm-nav-scrim]');
  if (mainNav && burgerBtn) {
    const desktopMq = window.matchMedia('(min-width: 1081px)');
    const setNavOpen = (open) => {
      mainNav.classList.toggle('open', open);
      burgerBtn.classList.toggle('is-open', open);
      burgerBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (navScrim) navScrim.classList.toggle('is-open', open);
      document.body.classList.toggle('tm-no-scroll', open);
    };

    burgerBtn.addEventListener('click', () => setNavOpen(!mainNav.classList.contains('open')));
    if (navScrim) navScrim.addEventListener('click', () => setNavOpen(false));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mainNav.classList.contains('open')) setNavOpen(false);
    });
    desktopMq.addEventListener('change', (mq) => {
      if (mq.matches) setNavOpen(false);
    });

    mainNav.addEventListener('click', (e) => {
      if (e.target.closest('[data-tm-nav-close]')) {
        setNavOpen(false);
        return;
      }
      const topLink = e.target.closest('.has-dropdown > .nav-link');
      if (topLink && !desktopMq.matches) {
        // Accordion: one sub-menu open at a time, top link does not navigate.
        e.preventDefault();
        const item = topLink.parentElement;
        const wasOpen = item.classList.contains('open');
        mainNav.querySelectorAll('.has-dropdown.open').forEach((el) => el.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
        return;
      }
      // Any real navigation from the drawer closes it.
      if (e.target.closest('a')) setNavOpen(false);
    });
  }

  // --- Shop filter drawer (mobile) -----------------------------------------
  // Server-rendered inside the products-shop block; Apply is a plain GET
  // submit, so only open/close behaviour lives here.
  const shopFilter = document.getElementById('tm-shop-filter');
  const shopFilterOpenBtn = document.querySelector('[data-tm-shop-filter-open]');
  const shopFilterScrim = document.querySelector('[data-tm-shop-filter-scrim]');
  if (shopFilter && shopFilterOpenBtn) {
    const setFilterOpen = (open) => {
      shopFilter.classList.toggle('is-open', open);
      shopFilter.setAttribute('aria-hidden', open ? 'false' : 'true');
      shopFilterOpenBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (shopFilterScrim) shopFilterScrim.classList.toggle('is-open', open);
      document.body.classList.toggle('tm-no-scroll', open);
      // Move focus with the drawer so keyboard users land inside it.
      if (open) {
        shopFilter.querySelector('.tm-shop-filter__close')?.focus();
      } else {
        shopFilterOpenBtn.focus();
      }
    };

    shopFilterOpenBtn.addEventListener('click', () => setFilterOpen(!shopFilter.classList.contains('is-open')));
    if (shopFilterScrim) shopFilterScrim.addEventListener('click', () => setFilterOpen(false));
    shopFilter.addEventListener('click', (e) => {
      if (e.target.closest('[data-tm-shop-filter-close]')) setFilterOpen(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && shopFilter.classList.contains('is-open')) setFilterOpen(false);
    });

    // Drop empty text fields on submit so applied URLs stay clean
    // (?pcat=power-tools instead of ?psearch=&pcat=power-tools).
    const shopFilterForm = shopFilter.querySelector('form');
    if (shopFilterForm) {
      shopFilterForm.addEventListener('submit', () => {
        shopFilterForm.querySelectorAll('input[type="text"]').forEach((input) => {
          if ('' === input.value.trim()) input.disabled = true;
        });
      });
    }
  }

  // --- Shop sort select auto-submits its GET form --------------------------
  const sortSelect = document.getElementById('sortSelect');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const form = sortSelect.closest('form');
      if (form) form.submit();
    });
  }

  // --- Contact enquiry form -------------------------------------------------
  // The form now submits to admin-post.php (real handler emails the enquiry and
  // redirects back with ?enquiry=sent, which render.php turns into a success
  // notice). If we land back with that flag, bring the notice into view.
  if (window.location.search.includes('enquiry=sent')) {
    document.getElementById('formSuccess')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // --- Fade-up scroll animations -------------------------------------------
  const fadeElements = document.querySelectorAll('.fade-up');
  if (fadeElements.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    fadeElements.forEach((el) => observer.observe(el));
  }

  // --- Mini-cart drawer ------------------------------------------------------
  // Shell + body are server-rendered (inc/mini-cart.php) and refresh through
  // WooCommerce cart fragments; the header cart button opens the drawer
  // instead of navigating (href stays as the no-JS fallback).
  const minicart = document.getElementById('tm-minicart');
  const cartOpenBtn = document.querySelector('[data-tm-cart-open]');
  const cartScrim = document.querySelector('[data-tm-cart-scrim]');
  if (minicart && cartOpenBtn) {
    const setCartOpen = (open) => {
      minicart.classList.toggle('is-open', open);
      minicart.setAttribute('aria-hidden', open ? 'false' : 'true');
      cartOpenBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (cartScrim) cartScrim.classList.toggle('is-open', open);
      document.body.classList.toggle('tm-no-scroll', open);
    };

    // Swap selector => outerHTML fragment maps (badge, drawer body) in place.
    const applyFragments = (fragments) => {
      Object.entries(fragments || {}).forEach(([selector, html]) => {
        // outerHTML (not replaceWith) parses the string as markup.
        document.querySelectorAll(selector).forEach((el) => {
          el.outerHTML = html;
        });
      });
    };

    // Pull the refreshed badge/drawer fragments from WC's own endpoint.
    const refreshFragments = () => {
      const url = new URL(minicart.dataset.tmCartAjax, window.location.href);
      url.searchParams.set('wc-ajax', 'get_refreshed_fragments');
      return fetch(url.toString(), { credentials: 'same-origin' })
        .then((res) => res.json())
        .then((json) => applyFragments(json?.fragments));
    };

    cartOpenBtn.addEventListener('click', (e) => {
      e.preventDefault();
      setCartOpen(!minicart.classList.contains('is-open'));
    });

    minicart.addEventListener('click', (e) => {
      if (e.target.closest('[data-tm-cart-close]')) setCartOpen(false);
    });
    if (cartScrim) cartScrim.addEventListener('click', () => setCartOpen(false));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && minicart.classList.contains('is-open')) setCartOpen(false);
    });

    // WooCommerce signals a finished AJAX add-to-cart with a jQuery event.
    // The parent theme adds through the Store API and fires it without
    // fragments, and wc-cart-fragments is not enqueued site-wide, so fetch
    // the refreshed fragments ourselves before revealing the drawer.
    if (window.jQuery) {
      window.jQuery(document.body).on('added_to_cart', () => {
        refreshFragments().finally(() => setCartOpen(true));
      });
    }

    // Qty stepper / remove: POST the new quantity (0 removes the line) to the
    // theme's wc-ajax endpoint, which answers with refreshed fragments to swap
    // in — wc-cart-fragments is not listening on every front-end page.
    minicart.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-tm-qty]');
      const removeBtn = e.target.closest('[data-tm-remove]');
      const btn = qtyBtn || removeBtn;
      if (!btn) return;
      const item = btn.closest('[data-cart-key]');
      if (!item) return;
      let next = 0;
      if (qtyBtn) {
        const value = item.querySelector('[data-tm-qty-value]');
        if (!value) return;
        next = parseInt(value.textContent, 10) + parseInt(qtyBtn.dataset.tmQty, 10);
        if (Number.isNaN(next) || next < 0) return;
      }
      btn.disabled = true;
      const body = new URLSearchParams({
        cart_item_key: item.dataset.cartKey,
        quantity: next,
        nonce: minicart.dataset.tmCartNonce || '',
      });
      fetch(minicart.dataset.tmCartAjax, { method: 'POST', body, credentials: 'same-origin' })
        .then((res) => res.json())
        .then((json) => {
          applyFragments(json?.data?.fragments);
        })
        .finally(() => {
          btn.disabled = false;
        });
    });
  }
});
